import { grossOf, isParentScoped, labelsMatch, rulePrices, type Labels, type PriceComponent, type PriceRule } from "./priceList";

/**
 * Plans › Components (plan builder design §9.4 C4, §9.11): every component an
 * operator sells, split into the ones sold ON THEIR OWN (the plans, and
 * anything with a price that needs no parent) and the ones sold ONLY WITH A
 * PLAN, each with its gate said in words.
 *
 * 🔑 The gate is read from the PRICE RULES, not from a dependency table. The
 * price-list compiler expresses "router rental needs a plan" as rules that
 * price the add-on only under a parent label selector, and radbilling v0.1.80
 * refuses (400) an add-on line whose parent no rule covers -- so the rules ARE
 * what the invoice path enforces. radbilling_component_dependency is not
 * written by the compiler and holds test rows only (phase4.test, 2026-10-09).
 *
 * Pure: no fetch, no React. The screen renders what this returns.
 */

/** One "sold with" condition: a parent predicate and what the add-on costs under it. */
export interface Gate {
  /** The parent predicate in words, e.g. "residential plans · 30 days". */
  words: string;
  /** The plan family the screen groups by ("residential plans", or a parent's own name). */
  family: string;
  /** The rest of the predicate ("30 days"); empty when the family says it all. */
  detail: string;
  /** The live components that satisfy it. Empty means the gate names nothing on this list. */
  parents: PriceComponent[];
  /** What the subscriber pays under that parent, in words ("₹299 incl. GST", "free", "included with the plan", "10% of the plan"). */
  price: string;
}

export interface ComponentEntry {
  component: PriceComponent;
  /** What a subscriber pays when no parent is involved; null when it is never sold that way. */
  ownPrice: string | null;
  gates: Gate[];
}

export interface ComponentsView {
  /** Sold on their own: at least one active rule without a parent, or no rule at all (the component price applies). */
  independent: ComponentEntry[];
  /** Sold only with a plan: every active rule is parent-scoped. */
  dependent: ComponentEntry[];
  /** Withdrawn components, counted rather than listed among what is on sale. */
  withdrawn: PriceComponent[];
}

const inr = (n: number) => `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/**
 * What a rule charges, in words, GST-inclusive like the published list.
 * The rule's amount is net of GST, at the component's own rate.
 */
export function priceWords(r: PriceRule, gstPercent: number): string {
  switch (r.pricingmode) {
    case "FREE":
      // Free but still a choice; INCLUDED comes with the plan (owner 2026-10-26, plan composition).
      return "free";
    case "INCLUDED":
      return "included with the plan";
    case "PERCENT_OF_PARENT":
      return `${r.amount}% of the plan`;
    default:
      return `${inr(grossOf(r.amount, gstPercent))} incl. GST`;
  }
}

const KNOWN: Record<string, (v: string) => string> = {
  term_days: (v) => `${v} day${v === "1" ? "" : "s"}`,
  speed_mbps: (v) => `${v} Mbps`,
};

/**
 * A parent label selector in words: `product` names the plan family, the
 * other known keys read as a term or a speed, anything else as key = value.
 * {product: "residential", term_days: "30"} → "residential plans · 30 days".
 */
export function selectorWords(sel: Labels): string {
  const { family, detail } = selectorParts(sel);
  return detail ? `${family} · ${detail}` : family;
}

function selectorParts(sel: Labels): { family: string; detail: string } {
  const rest = Object.keys(sel)
    .filter((k) => k !== "product")
    .sort()
    .map((k) => (KNOWN[k] ? KNOWN[k](sel[k]!) : `${k} = ${sel[k]}`));
  return { family: sel.product ? `${sel.product} plans` : "plans", detail: rest.join(" · ") };
}

function gateOf(r: PriceRule, gstPercent: number, live: PriceComponent[]): Gate {
  if (r.parentcomponentid) {
    const parent = live.find((c) => c.id === r.parentcomponentid);
    const words = parent ? parent.name : "a component not on this list";
    return {
      words,
      family: words,
      detail: "",
      parents: parent ? [parent] : [],
      price: priceWords(r, gstPercent),
    };
  }
  const sel = r.parentlabelselector ?? {};
  return {
    words: selectorWords(sel),
    ...selectorParts(sel),
    parents: live.filter((c) => labelsMatch(sel, c.labels)),
    price: priceWords(r, gstPercent),
  };
}

const byName = (a: ComponentEntry, b: ComponentEntry) => a.component.name.localeCompare(b.component.name);

export function componentsView(components: PriceComponent[], rules: PriceRule[]): ComponentsView {
  const live = components.filter((c) => c.active);
  const activeRules = rules.filter((r) => r.active);
  const independent: ComponentEntry[] = [];
  const dependent: ComponentEntry[] = [];

  for (const c of live) {
    const mine = activeRules.filter((r) => rulePrices(r, c));
    const own = mine.filter((r) => !isParentScoped(r));
    // Newest rule first, so the price shown is the one that took effect last,
    // and one gate per parent predicate: a re-priced rule is a new version
    // under the same parent, not a second way to buy.
    const newestFirst = (a: PriceRule, b: PriceRule) => b.effectivefrom.localeCompare(a.effectivefrom) || b.version - a.version;
    own.sort(newestFirst);
    const seen = new Set<string>();
    const gated = mine
      .filter(isParentScoped)
      .sort(newestFirst)
      .filter((r) => {
        const key = r.parentcomponentid ?? JSON.stringify(Object.entries(r.parentlabelselector ?? {}).sort());
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    const latest = own[0];
    const entry: ComponentEntry = {
      component: c,
      // No rule at all: radbilling bills the component's own price.
      ownPrice: mine.length === 0 ? `${inr(grossOf(c.price, c.gstpercent))} incl. GST` : latest ? priceWords(latest, c.gstpercent) : null,
      gates: gated.map((r) => gateOf(r, c.gstpercent, live)),
    };
    (entry.ownPrice === null ? dependent : independent).push(entry);
  }

  return {
    independent: independent.sort(byName),
    dependent: dependent.sort(byName),
    withdrawn: components.filter((c) => !c.active).sort((a, b) => a.name.localeCompare(b.name)),
  };
}
